import posthog from 'posthog-js';

/**
 * A failed input check surfaced to the user (a toast, an inline field error).
 * Thrown where validation has to unwind an async submit path, so the caller
 * can tell "the user must fix something" apart from "the operation broke".
 * Never reported to error tracking.
 */
export class ValidationError extends Error {
  readonly isValidationError = true;

  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

/**
 * Builds the error to throw for a failed fetch. Routes flag the rejections the
 * user has to fix — a malformed address, an unsupported file — with
 * `userInput: true`, and those become ValidationErrors so they stay out of
 * error tracking. Everything else stays a plain Error and is reported.
 */
export function responseError(message: string, body?: { userInput?: boolean } | null): Error {
  return body?.userInput ? new ValidationError(message) : new Error(message);
}

/**
 * Reports a caught error to PostHog error tracking. Client-side only:
 * silently no-ops when PostHog isn't initialized (server render, missing key).
 *
 * Quota guards (free tier — every $exception also records a session replay):
 * - at most one capture per operation and MAX_CAPTURES_PER_SESSION total per
 *   page load, so retry loops or an API outage can't drain the budget;
 *   pass { force: true } to exempt a capture (render crashes)
 * - fully disabled via setCaptureErrorEnabled(false) (widget test mode)
 * - AbortError (navigation/unmount), SecurityError (storage blocked in
 *   embedded iframes), network TypeErrors ("Failed to fetch" / "Load failed"),
 *   ValidationError and server-side validation errors (fieldErrors) are
 *   skipped: user input, teardown, connectivity or browser policy — not defects
 */
const MAX_CAPTURES_PER_SESSION = 3;
const MAX_ERROR_TEXT_LENGTH = 500;

let enabled = true;
const capturedOperations = new Set<string>();

export function setCaptureErrorEnabled(value: boolean): void {
  enabled = value;
}

export function captureError(
  err: unknown,
  context: { operation: string } & Record<string, unknown>,
  opts?: { force?: boolean }
): void {
  if (!posthog.__loaded || !enabled) return;
  const errName = (err as { name?: string } | null)?.name;
  if (errName === 'AbortError' || errName === 'SecurityError') return;
  // fetch() rejects with these when the connection drops or the page unloads mid-request:
  // the caller's own retry/requeue handles them, and they are not defects.
  const errMessage = (err as { message?: string } | null)?.message;
  if (errName === 'TypeError' && (errMessage === 'Failed to fetch' || errMessage === 'Load failed')) return;
  if ((err as { isValidationError?: boolean } | null)?.isValidationError) return;
  if ((err as { fieldErrors?: unknown } | null)?.fieldErrors) return;
  if (!opts?.force) {
    if (capturedOperations.size >= MAX_CAPTURES_PER_SESSION) return;
    if (capturedOperations.has(context.operation)) return;
    capturedOperations.add(context.operation);
  }
  const error = err instanceof Error ? err : new Error(String(err));
  posthog.captureException(error, context);
}

/**
 * Reports a non-OK HTTP response for flows whose failure branch doesn't throw.
 * Builds a uniform Error so PostHog grouping keys on the operation, not prose.
 */
export function captureHttpError(
  operation: string,
  status: number,
  context?: Record<string, unknown> & { errorText?: string }
): void {
  captureError(new Error(`HTTP ${status}`), {
    ...context,
    errorText:
      typeof context?.errorText === 'string'
        ? context.errorText.slice(0, MAX_ERROR_TEXT_LENGTH)
        : undefined,
    operation,
    status,
  });
}
